export const PLANNER_RECENTS_DATE_PRESETS = Object.freeze({
  allTime: 'allTime',
  last7Days: 'last7Days',
  last30Days: 'last30Days',
  thisYear: 'thisYear',
  custom: 'custom',
});

export const PLANNER_RECENTS_DATE_PRESET_LABELS = Object.freeze({
  [PLANNER_RECENTS_DATE_PRESETS.allTime]: 'All Time',
  [PLANNER_RECENTS_DATE_PRESETS.last7Days]: 'Last 7 Days',
  [PLANNER_RECENTS_DATE_PRESETS.last30Days]: 'Last 30 Days',
  [PLANNER_RECENTS_DATE_PRESETS.thisYear]: 'This Year',
  [PLANNER_RECENTS_DATE_PRESETS.custom]: 'Custom Range',
});

const localDateParts = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, monthIndex, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== monthIndex ||
    date.getDate() !== day
  ) {
    return null;
  }
  return {year, monthIndex, day};
};

export const formatPlannerLocalDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const plannerCustomLocalDateBounds = (
  startLocalDate: string,
  endLocalDate: string,
) => {
  const start = localDateParts(startLocalDate);
  const end = localDateParts(endLocalDate);
  if (!start || !end) return null;

  const startDate = new Date(start.year, start.monthIndex, start.day);
  const endDateExclusive = new Date(end.year, end.monthIndex, end.day + 1);
  if (startDate >= endDateExclusive) return null;

  return {
    startDate: startDate.toISOString(),
    endDate: endDateExclusive.toISOString(),
  };
};

export const buildPlannerRecentsDateBounds = (
  preset: string,
  {now = new Date()}: {now?: Date} = {},
) => {
  if (preset === PLANNER_RECENTS_DATE_PRESETS.allTime) {
    return {startDate: null, endDate: null};
  }

  const localToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let localStart: Date;
  let localEndExclusive: Date;

  if (preset === PLANNER_RECENTS_DATE_PRESETS.last7Days) {
    localStart = new Date(
      localToday.getFullYear(),
      localToday.getMonth(),
      localToday.getDate() - 6,
    );
    localEndExclusive = new Date(
      localToday.getFullYear(),
      localToday.getMonth(),
      localToday.getDate() + 1,
    );
  } else if (preset === PLANNER_RECENTS_DATE_PRESETS.last30Days) {
    localStart = new Date(
      localToday.getFullYear(),
      localToday.getMonth(),
      localToday.getDate() - 29,
    );
    localEndExclusive = new Date(
      localToday.getFullYear(),
      localToday.getMonth(),
      localToday.getDate() + 1,
    );
  } else if (preset === PLANNER_RECENTS_DATE_PRESETS.thisYear) {
    localStart = new Date(localToday.getFullYear(), 0, 1);
    localEndExclusive = new Date(
      localToday.getFullYear(),
      localToday.getMonth(),
      localToday.getDate() + 1,
    );
  } else {
    return {startDate: null, endDate: null};
  }

  return {
    startDate: localStart.toISOString(),
    endDate: localEndExclusive.toISOString(),
  };
};

export const isPlannerDateWithinBounds = (
  value: Date | string | number | null | undefined,
  {startDate = null, endDate = null}: Record<string, any> = {},
) => {
  if (!startDate && !endDate) return true;
  const timestamp =
    value instanceof Date ? value.getTime() : new Date(value!).getTime();
  if (!Number.isFinite(timestamp)) return false;
  if (startDate && timestamp < new Date(startDate).getTime()) return false;
  if (endDate && timestamp >= new Date(endDate).getTime()) return false;
  return true;
};
